import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import api from '@crm-src/lib/api'
import type { Task, TaskStats, TaskFilters, CreateTaskData, UpdateTaskData, TaskPagination } from '@crm-src/types/task'

interface TasksState {
  tasks: Task[]
  isLoading: boolean
  error: string | null
  stats: TaskStats | null
  calendarTasks: Task[]
  pagination: TaskPagination | null
  lastFetch: number | null
}

const initialState: TasksState = {
  tasks: [],
  isLoading: false,
  error: null,
  stats: null,
  calendarTasks: [],
  pagination: null,
  lastFetch: null
}

// Async thunks

// Fetch tasks with filters
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (filters: TaskFilters = {}) => {
    const response = await api.get('/tasks', { params: filters })
    return response.data
  }
)

// Fetch tasks for calendar view
export const fetchCalendarTasks = createAsyncThunk(
  'tasks/fetchCalendarTasks',
  async (params: { start_date?: string; end_date?: string; assigned_to_id?: number; status?: string }) => {
    const response = await api.get('/tasks/calendar', { params })
    return response.data
  }
)

// Fetch task stats
export const fetchTaskStats = createAsyncThunk(
  'tasks/fetchTaskStats',
  async (params: { assigned_to_id?: number; user_id?: number } = {}) => {
    const response = await api.get('/tasks/stats', { params })
    return response.data
  }
)

// Create new task
export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (taskData: CreateTaskData) => {
    // Extract recurring pattern fields
    const {
      is_recurring,
      recurrence_pattern,
      recurrence_interval,
      recurrence_end_date,
      recurrence_count,
      ...taskFields
    } = taskData as any
    
    // Build the request payload
    const payload: any = {
      task: {
        ...taskFields,
        is_recurring: is_recurring || false
      }
    }
    
    // If recurring, add the pattern as a separate key
    if (is_recurring && recurrence_pattern) {
      payload.recurring_pattern = {
        recurring_type: recurrence_pattern,
        repeat_interval: recurrence_interval || 1,
        starting_at: taskFields.due_date,
        ending_at: recurrence_end_date || null,
        max_occurrences: recurrence_count || null
      }
    }
    
    const response = await api.post('/tasks', payload)
    return response.data
  }
)

// Update task
export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ id, data }: { id: string; data: UpdateTaskData }) => {
    // Extract recurring pattern fields
    const {
      is_recurring,
      recurrence_pattern,
      recurrence_interval,
      recurrence_end_date,
      recurrence_count,
      ...taskFields
    } = data as any
    
    // Build the request payload
    const payload: any = {
      task: {
        ...taskFields,
        is_recurring: is_recurring || false
      }
    }
    
    // If recurring, add the pattern as a separate key
    if (is_recurring && recurrence_pattern) {
      payload.recurring_pattern = {
        recurring_type: recurrence_pattern,
        repeat_interval: recurrence_interval || 1,
        starting_at: taskFields.due_date || new Date().toISOString(),
        ending_at: recurrence_end_date || null,
        max_occurrences: recurrence_count || null
      }
    }
    
    const response = await api.put(`/tasks/${id}`, payload)
    return response.data
  }
)

// Toggle task completion
export const toggleTaskComplete = createAsyncThunk(
  'tasks/toggleTaskComplete',
  async (id: string) => {
    const response = await api.post(`/tasks/${id}/toggle_complete`)
    return response.data
  }
)

// Delete task
export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (id: string) => {
    await api.delete(`/tasks/${id}`)
    return id
  }
)

// Cancel a specific occurrence of a recurring task
export const cancelTaskOccurrence = createAsyncThunk(
  'tasks/cancelTaskOccurrence',
  async ({ taskId, occurrenceDate }: { taskId: string; occurrenceDate: string }) => {
    await api.post(`/tasks/${taskId}/occurrences/${occurrenceDate}/cancel`)
    return { taskId, occurrenceDate }
  }
)

// Get single task
export const fetchTask = createAsyncThunk(
  'tasks/fetchTask',
  async (id: string) => {
    const response = await api.get(`/tasks/${id}`)
    return response.data
  }
)

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCache: (state) => {
      state.tasks = []
      state.calendarTasks = []
      state.stats = null
      state.pagination = null
      state.lastFetch = null
    },
    // Optimistic update for toggle completion
    optimisticToggleComplete: (state, action: PayloadAction<string>) => {
      const task = state.tasks.find(t => t.id === action.payload)
      if (task) {
        if (task.status === 'completed') {
          task.status = 'pending'
          task.completed_at = undefined
        } else {
          task.status = 'completed'
          task.completed_at = new Date().toISOString()
        }
      }
      
      // Also update in calendar tasks
      const calendarTask = state.calendarTasks.find(t => t.id === action.payload)
      if (calendarTask) {
        if (calendarTask.status === 'completed') {
          calendarTask.status = 'pending'
          calendarTask.completed_at = undefined
        } else {
          calendarTask.status = 'completed'
          calendarTask.completed_at = new Date().toISOString()
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.isLoading = false
        state.tasks = action.payload.data || []
        state.pagination = action.payload.meta?.pagination || null
        state.stats = action.payload.stats || state.stats
        state.lastFetch = Date.now()
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch tasks'
      })
      
      // Fetch calendar tasks
      .addCase(fetchCalendarTasks.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCalendarTasks.fulfilled, (state, action) => {
        state.isLoading = false
        state.calendarTasks = action.payload.data || []
      })
      .addCase(fetchCalendarTasks.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch calendar tasks'
      })
      
      // Fetch task stats
      .addCase(fetchTaskStats.pending, (state) => {
        // Don't set loading for stats - it's usually a background fetch
      })
      .addCase(fetchTaskStats.fulfilled, (state, action) => {
        // Handle both old format { stats: {...} } and new ApiResponse format { data: { stats: {...} } }
        const payload = action.payload
        state.stats = payload?.data?.stats || payload?.stats || null
      })
      .addCase(fetchTaskStats.rejected, (state, action) => {
        // Silently fail for stats - endpoint might not exist (404)
        // Don't log 404 errors for stats endpoint
        const error = action.error as any
        const is404 = error?.response?.status === 404 || error?.message?.includes('404')
        if (!is404) {
          console.warn('Failed to fetch task stats:', action.error)
        }
        // Keep existing stats or set to null
        state.stats = state.stats || null
      })
      
      // Create task
      .addCase(createTask.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.isLoading = false
        if (action.payload.data) {
          state.tasks.unshift(action.payload.data)
        }
      })
      .addCase(createTask.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to create task'
      })
      
      // Update task
      .addCase(updateTask.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.isLoading = false
        if (action.payload.data) {
          const index = state.tasks.findIndex(t => t.id === action.payload.data.id)
          if (index !== -1) {
            state.tasks[index] = action.payload.data
          }
          
          const calendarIndex = state.calendarTasks.findIndex(t => t.id === action.payload.data.id)
          if (calendarIndex !== -1) {
            state.calendarTasks[calendarIndex] = action.payload.data
          }
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to update task'
      })
      
      // Toggle task completion
      .addCase(toggleTaskComplete.fulfilled, (state, action) => {
        // Handle ApiResponse format: { success: true, data: taskData, message: ... }
        // or direct taskData format
        const taskData = action.payload.data || action.payload
        if (taskData && taskData.id) {
          const taskId = taskData.id?.toString() || String(taskData.id)
          
          if (taskId) {
            // Find task by ID (handles both regular tasks and recurring instances)
            const index = state.tasks.findIndex(t => String(t.id) === taskId)
            if (index !== -1) {
              // Update the task with the new data, replacing all properties to ensure status is correct
              state.tasks[index] = { ...taskData }
            }
            
            // Also update in calendar tasks
            const calendarIndex = state.calendarTasks.findIndex(t => String(t.id) === taskId)
            if (calendarIndex !== -1) {
              state.calendarTasks[calendarIndex] = { ...taskData }
            }
          }
        }
      })
      .addCase(toggleTaskComplete.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to toggle task completion'
      })
      
      // Delete task
      .addCase(deleteTask.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.isLoading = false
        state.tasks = state.tasks.filter(t => t.id !== action.payload)
        state.calendarTasks = state.calendarTasks.filter(t => t.id !== action.payload)
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to delete task'
      })
      
      // Cancel task occurrence
      .addCase(cancelTaskOccurrence.fulfilled, (state, action) => {
        // The occurrence is now cancelled, refetch will show updated calendar
        state.isLoading = false
      })
      .addCase(cancelTaskOccurrence.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to cancel occurrence'
      })
      
      // Fetch single task
      .addCase(fetchTask.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.tasks.findIndex(t => t.id === action.payload.data.id)
          if (index !== -1) {
            state.tasks[index] = action.payload.data
          } else {
            state.tasks.push(action.payload.data)
          }
        }
      })
  }
})

export const { clearError, clearCache, optimisticToggleComplete } = tasksSlice.actions
export default tasksSlice.reducer

// Selectors
export const selectTasks = (state: { tasks: TasksState }) => state.tasks.tasks
export const selectCalendarTasks = (state: { tasks: TasksState }) => state.tasks.calendarTasks
export const selectTasksLoading = (state: { tasks: TasksState }) => state.tasks.isLoading
export const selectTasksError = (state: { tasks: TasksState }) => state.tasks.error
export const selectTaskStats = (state: { tasks: TasksState }) => state.tasks.stats
export const selectTasksPagination = (state: { tasks: TasksState }) => state.tasks.pagination



